// Progress (today's plans/Progress.tsx): a bar for each phase by stage, in the board's state colours and
// icons, a tooltip on each segment on hover and on focus, and the same
// numbers as a table.
import { useLayoutEffect, useRef, useState, type FocusEvent, type PointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { StateIcon } from '../components/Icons'
import { PHASE_ICON, STAGE_ICON, STAGES, type PhaseFacts } from './state'
import { planWords as w } from './words'

interface Tip {
  text: string
  x: number
  y: number
}

export function Progress({ phases, onGo }: { phases: readonly PhaseFacts[]; onGo: (id: string) => void }) {
  const [tip, setTip] = useState<Tip | null>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const widest = Math.max(1, ...phases.map((p) => p.total))

  // Kept inside the window: centred above its segment, moved in from an edge.
  useLayoutEffect(() => {
    const element = tipRef.current
    if (!tip || !element) return
    const width = element.offsetWidth
    const left = Math.min(Math.max(8, tip.x - width / 2), Math.max(8, window.innerWidth - width - 8))
    element.style.left = `${left}px`
    element.style.top = `${Math.max(8, tip.y - element.offsetHeight - 8)}px`
  }, [tip])

  const show = (event: PointerEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
    const target = event.currentTarget
    const rect = target.getBoundingClientRect()
    setTip({ text: target.dataset.tip ?? '', x: rect.left + rect.width / 2, y: rect.top })
  }
  const hide = () => setTip(null)

  return (
    <section className="pv-progress" id="pv-progress">
      <h2>{w.progress}</h2>
      <figure className="pv-chart" aria-label={w.chartLabel}>
        <figcaption>
          <span className="pv-chart-title">{w.chartTitle}</span>
          <span className="pv-legend">
            {STAGES.map((stage) => (
              <span key={stage} className="pv-legend-item">
                <StateIcon state={STAGE_ICON[stage]} size={12} />
                {w.stage[stage]}
              </span>
            ))}
          </span>
        </figcaption>
        {phases.map((p) => (
          // The whole row goes to its phase for a pointer; its name is the link for the keyboard.
          <div
            key={p.phase.id}
            className="pv-bar-row"
            onClick={(event) => {
              if (!(event.target as Element).closest('a')) onGo(p.phase.id)
            }}
          >
            <a className="pv-bar-name" href={`#${p.phase.id}`}>
              <StateIcon state={PHASE_ICON[p.state]} size={12} />
              {p.phase.title}
            </a>
            <span className="pv-bar-track">
              {STAGES.map((stage) => {
                const n = p.counts[stage]
                if (!n) return null
                return (
                  <span
                    key={stage}
                    className="pv-seg"
                    data-stage={stage}
                    style={{ width: `${((100 * n) / widest).toFixed(2)}%` }}
                    tabIndex={0}
                    role="img"
                    data-tip={w.segmentTip(p.phase.title, w.stage[stage], n, p.total)}
                    aria-label={w.segmentLabel(w.stage[stage], n, p.total)}
                    onPointerEnter={show}
                    onFocus={show}
                    onPointerLeave={hide}
                    onBlur={hide}
                  />
                )
              })}
            </span>
            <span className="pv-bar-value">{p.total ? w.barValue(p.landed, p.total) : w.noIssuesYet}</span>
          </div>
        ))}
      </figure>
      <details className="pv-fold pv-chart-table">
        <summary>{w.asTable}</summary>
        <div className="pv-table-wrap">
          <table className="pv-issues-table">
            <thead>
              <tr>
                <th scope="col">{w.phase}</th>
                {STAGES.map((stage) => (
                  <th key={stage} scope="col" className="pv-num">
                    {w.stage[stage]}
                  </th>
                ))}
                <th scope="col" className="pv-num">
                  {w.issues}
                </th>
              </tr>
            </thead>
            <tbody>
              {phases.map((p) => (
                <tr key={p.phase.id}>
                  <th scope="row">{p.phase.title}</th>
                  {STAGES.map((stage) => (
                    <td key={stage} className="pv-num">
                      {p.counts[stage]}
                    </td>
                  ))}
                  <td className="pv-num">{p.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      {/* Above everything, and placed against the window: the page scrolls inside a box of its own. */}
      {tip &&
        createPortal(
          <div ref={tipRef} className="pv-tip" role="tooltip">
            {tip.text}
          </div>,
          document.body,
        )}
    </section>
  )
}
